import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiResponse } from '../../_model/api-response';
import {
  AdminKycApplicationDetailsDto,
  AdminKycApplicationListItemDto,
  ComplianceActivityLimitDto,
  ComplianceDecisionDto,
  ComplianceTierTableDto,
  ConfirmDocumentUploadRequest,
  CreateDocumentUploadUrlRequest,
  CreateDocumentUploadUrlResponse,
  DeleteKycDocumentResponseDto,
  DocumentDownloadUrlDto,
  KycApplicationReviewResultDto,
  KycDocumentReviewResultDto,
  KycDocumentSummaryDto,
  ResubmitKycApplicationRequestDto,
  SaveKycFieldValuesRequestDto,
  SaveKycFieldValuesResponseDto,
  StartKycApplicationResponseDto,
  SubmitKycApplicationRequestDto,
  UpdateComplianceActivityLimitRequest,
  UploadKycDocumentRequestDto,
  UploadKycDocumentResponseDto,
  UserKycApplicationDetailsDto,
  UserKycApplicationListItemDto,
  ValidateKycApplicationRequestDto,
  ValidateKycApplicationResponseDto
} from '../../_model/compliance';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ComplianceApiService {
  private http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl + '/Compliance';
  private pageSize = environment.pageSize;

  private unwrap<T>(res: ApiResponse<T>): T {
    return (res?.data) as T;
  }

  getBootstrap(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/bootstrap`);
  }

  startApplication(): Observable<ApiResponse<StartKycApplicationResponseDto>> {
    return this.http.post<ApiResponse<StartKycApplicationResponseDto>>(
      `${this.baseUrl}/applications/start`,
      {}
    );
  }

  getMyApplications(filters: {
    status?: string;
    jurisdictionCode?: string;
    tierCode?: string;
    productType?: string;
    page?: number;
  }): Observable<ApiResponse<UserKycApplicationListItemDto[]>> {
    let params = new HttpParams()
      .set('pageSize', this.pageSize);

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this.http.get<ApiResponse<UserKycApplicationListItemDto[]>>(
      `${this.baseUrl}/applications`,
      { params }
    );
  }

  getApplicationById(applicationId: string): Observable<ApiResponse<UserKycApplicationDetailsDto>> {
    return this.http.get<ApiResponse<UserKycApplicationDetailsDto>>(
      `${this.baseUrl}/applications/${applicationId}`
    );
  }

  saveFieldValues(
    applicationId: string,
    payload: SaveKycFieldValuesRequestDto
  ): Observable<ApiResponse<SaveKycFieldValuesResponseDto>> {
    return this.http.post<ApiResponse<SaveKycFieldValuesResponseDto>>(
      `${this.baseUrl}/applications/${applicationId}/fields`,
      payload
    );
  }

  validateApplication(
    applicationId: string,
    payload: ValidateKycApplicationRequestDto): Observable<ApiResponse<ValidateKycApplicationResponseDto>> {
    return this.http.post<ApiResponse<ValidateKycApplicationResponseDto>>(
      `${this.baseUrl}/applications/${applicationId}/validate`,
      payload
    );
  }

  submitApplication(
    applicationId: string,
    payload: SubmitKycApplicationRequestDto = { forceResubmitChecks: true }
  ): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.baseUrl}/applications/${applicationId}/submit`,
      payload
    );
  }

  uploadDocument(
    applicationId: string,
    payload: UploadKycDocumentRequestDto
  ): Observable<ApiResponse<UploadKycDocumentResponseDto>> {
    return this.http.post<ApiResponse<UploadKycDocumentResponseDto>>(
      `${this.baseUrl}/applications/${applicationId}/documents`,
      payload
    );
  }

  getDocuments(applicationId: string): Observable<ApiResponse<KycDocumentSummaryDto[]>> {
    return this.http.get<ApiResponse<KycDocumentSummaryDto[]>>(
      `${this.baseUrl}/applications/${applicationId}/documents`
    );
  }

  deleteDocument(
    applicationId: string,
    documentId: string
  ): Observable<ApiResponse<DeleteKycDocumentResponseDto>> {
    return this.http.delete<ApiResponse<DeleteKycDocumentResponseDto>>(
      `${this.baseUrl}/applications/${applicationId}/documents/${documentId}`
    );
  }

  resubmit(
    applicationId: string,
    payload: ResubmitKycApplicationRequestDto = { clearPreviousReviewNotes: true }
  ): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.baseUrl}/applications/${applicationId}/resubmit`,
      payload
    );
  }

  getMyTierTable(): Observable<ApiResponse<ComplianceTierTableDto>> {
    return this.http.get<ApiResponse<ComplianceTierTableDto>>(
      `${this.baseUrl}/tiers/table/me`
    );
  }

  getPublicTierTable(
    jurisdictionCode: string,
    customerType: string,
    productType: string
  ): Observable<ApiResponse<ComplianceTierTableDto>> {

    let params = new HttpParams()
      .set('jurisdictionCode', jurisdictionCode)
      .set('customerType', customerType)
      .set('productType', productType);

    return this.http.get<ApiResponse<ComplianceTierTableDto>>(
      `${this.baseUrl}/tiers/table`,
      { params }
    );
  }

  canCreateWallet(jurisdictionCode: string): Observable<ApiResponse<ComplianceDecisionDto>> {
    return this.http.get<ApiResponse<ComplianceDecisionDto>>(
      `${this.baseUrl}/decisions/wallet`,
      { params: { jurisdictionCode } as any }
    );
  }

  getAdminReviews(filters: {
    status?: string;
    jurisdictionCode?: string;
    tierCode?: string;
    customerType?: string;
    productType?: string;
    search?: string;
    page?: number
  }): Observable<ApiResponse<AdminKycApplicationListItemDto[]>> {
    let params = new HttpParams()
      .set('pageSize', this.pageSize);

    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return this.http.get<ApiResponse<AdminKycApplicationListItemDto[]>>(
      `${this.baseUrl}/admin/kyc/reviews`,
      { params }
    );
  }

  getAdminReviewById(applicationId: string): Observable<ApiResponse<AdminKycApplicationDetailsDto>> {
    return this.http.get<ApiResponse<AdminKycApplicationDetailsDto>>(
      `${this.baseUrl}/admin/kyc/reviews/${applicationId}`
    );
  }

  startReview(
    applicationId: string,
    reviewNotes?: string
  ): Observable<ApiResponse<KycApplicationReviewResultDto>> {
    return this.http.post<ApiResponse<KycApplicationReviewResultDto>>(
      `${this.baseUrl}/admin/kyc/reviews/${applicationId}/start-review`,
      { reviewNotes: reviewNotes || null }
    );
  }

  approveApplication(
    applicationId: string,
    reviewNotes?: string
  ): Observable<ApiResponse<KycApplicationReviewResultDto>> {
    return this.http.post<ApiResponse<KycApplicationReviewResultDto>>(
      `${this.baseUrl}/admin/kyc/reviews/${applicationId}/approve`,
      { reviewNotes: reviewNotes || null }
    );
  }

  rejectApplication(
    applicationId: string,
    reason: string
  ): Observable<ApiResponse<KycApplicationReviewResultDto>> {
    return this.http.post<ApiResponse<KycApplicationReviewResultDto>>(
      `${this.baseUrl}/admin/kyc/reviews/${applicationId}/reject`,
      { reason }
    );
  }

  approveDocument(
    documentId: string,
    reviewNotes?: string
  ): Observable<ApiResponse<KycDocumentReviewResultDto>> {
    return this.http.post<ApiResponse<KycDocumentReviewResultDto>>(
      `${this.baseUrl}/admin/kyc/reviews/documents/${documentId}/approve`,
      { reviewNotes: reviewNotes || null }
    );
  }

  rejectDocument(
    documentId: string,
    reason: string
  ): Observable<ApiResponse<KycDocumentReviewResultDto>> {
    return this.http.post<ApiResponse<KycDocumentReviewResultDto>>(
      `${this.baseUrl}/admin/kyc/reviews/documents/${documentId}/reject`,
      { reason }
    );
  }

  getAdminTierTable(
    jurisdictionCode: string,
    customerType: string,
    productType: string
  ): Observable<ApiResponse<ComplianceTierTableDto>> {
    let params = new HttpParams()
      .set('jurisdictionCode', jurisdictionCode)
      .set('customerType', customerType)
      .set('productType', productType);

    return this.http.get<ApiResponse<ComplianceTierTableDto>>(
      `${this.baseUrl}/admin/tiers`,
      { params }
    );
  }

  updateAdminTierLimit(
    payload: UpdateComplianceActivityLimitRequest
  ): Observable<ComplianceActivityLimitDto> {
    return this.http
      .post<ApiResponse<ComplianceActivityLimitDto>>(
        `${this.baseUrl}/admin/tiers/limits`,
        payload
      )
      .pipe(map(res => this.unwrap(res)));
  }

  createDocumentUploadUrl(
    applicationId: string,
    payload: CreateDocumentUploadUrlRequest
  ): Observable<ApiResponse<CreateDocumentUploadUrlResponse>> {
    return this.http.post<ApiResponse<CreateDocumentUploadUrlResponse>>(
      `${this.baseUrl}/applications/${applicationId}/documents/presign`,
      payload
    );
  }

  uploadFileToS3(uploadUrl: string, file: File): Observable<any> {
    return this.http.put(uploadUrl, file, {
      headers: new HttpHeaders({
        'Content-Type': file.type || 'application/octet-stream'
      }),
      responseType: 'text'
    });
  }

  confirmDocumentUpload(applicationId: string, payload: ConfirmDocumentUploadRequest) {
    return this.http.post(
      `${this.baseUrl}/applications/${applicationId}/documents/confirm`,
      payload
    );
  }

  getDocumentDownloadUrl(documentId: string) {
    return this.http.get<DocumentDownloadUrlDto>(
      `${this.baseUrl}/documents/${documentId}/download-url`
    );
  }

  deleteApplication(applicationId: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.baseUrl}/applications/${applicationId}`);
  }

  syncThirdPartyVerification(applicationId: string) {
    return this.http.post(
      `${this.baseUrl}/admin/applications/${applicationId}/sync-third-party`,
      {}
    );
  }

  reopenApplication(
    applicationId: string,
    reason: string
  ) {
    return this.http.post<ApiResponse<any>>(
      `${this.baseUrl}/admin/kyc/reviews/${applicationId}/reopen`,
      {
        reason
      });
  }
}